<template>
    <div class="card-notes">
        <div class="note-container">
            <div class="note">
                <div class="text-muted small fw-600 mb-2">INTERNAL NOTES, PHOTOS AND FILES</div>
                <button type="button" class="btn btn-light" @click="clickedAddNote">
                    <i class="fas fa-plus"></i> Add note
                </button>
                
                <button type="button" class="btn btn-light" :class="uploadingImage ? 'disabled' : ''" @click="clickedAddPhoto">
                    <span v-if="uploadingImage" class="loader loader-xs mr-2"></span><i class="fas fa-camera"></i> Add photo
                </button>
                <input :ref="inputImageRef" :id="imagePickerId" type="file" accept="image/*" class="spark-uploader-control hide-input" name="image" @change="uploadImage" >

                <button type="button" class="btn btn-light" @click="clickedAttachFile">
                    <i class="fas fa-paperclip"></i> Attach a file
                </button>
                <input :ref="inputFileRef" :id="filePickerId" type="file" accept="image/*,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.txt,.pdf,.csv" class="spark-uploader-control hide-input" name="file" @change="selectedFile" >

            </div>
        </div>
        <div class="note-container" v-show="showNoteInput">
            <div class="note">
                <textarea v-model="noteContent"
                          type="text"
                          rows="5"
                          class="form-control"
                          :disabled="savingNote"
                          placeholder="Add a note..." 
                          name="note"></textarea>
                <div class="mt-2 text-muted">
                    Notes are for internal use and are not visible to the customer.
                </div>
                <div class="mt-2">
                    <button type="button" class="btn btn-light" :disabled="savingNote" @click="clickedCancelNote">
                        Cancel
                    </button>
                    <button type="button" class="btn btn-primary" :disabled="(savingNote || noteContent.length == 0)" @click="clickedSaveNote">
                        <span v-if="savingNote" class="loader loader-xs light mr-1"></span> Add note
                    </button>
                </div>
            </div>
        </div>
        <div class="note-container" v-show="showFileTitleInput">
            <div class="note">
                <div class="row no-gutters">
                    <div class="col-md-2">
                        <button type="button" class="btn full-width" :class="fileName ? 'btn-success' : 'btn-secondary'" :disabled="uploadingFile" @click="clickedChooseFile">
                                <span v-if="fileName">{{ fileName }}</span>
                                <span v-else>Choose file</span>
                        </button>
                    </div>
                    <div class="col-md-10">
                        <input  v-model="fileTitle" 
                                type="text"
                                rows="1"
                                class="form-control ml-2"
                                :disabled="uploadingFile"
                                placeholder="Include a comment with this file..." 
                                name="note"/>
                    </div>
                </div>
                <div class="mt-2 text-muted">
                    Files are for internal use and are not visible to the customer.
                </div>
                <div class="mt-2">
                    <button type="button" class="btn btn-light" :disabled="uploadingFile" @click="clickedCancelNote">
                        Cancel
                    </button>
                    <button type="button" class="btn btn-primary" :disabled="(uploadingFile || !file)" @click="clickedUploadFile">
                        <span v-if="uploadingFile" class="loader loader-xs light mr-1"></span> Save file
                    </button>
                </div>
            </div>
        </div>

        <!-- File list -->
        <div class="files" v-if="hasFileAttachments">
            <h6>Files</h6>
            <div v-for="file in files" class="note-container" :key="file.id + '-file'">
                <!-- FILE -->
                <div class="note">
                    <div class="row no-gutters">
                        
                        <!-- ICON -->
                        <div v-if="isPDF(file)" class="col-md-1 icon text-muted"> <!-- Extra div required for conditional FA icons -->
                            <div>
                                <i class="fas fa-file-pdf"></i>
                            </div>
                        </div>
                        <div v-else-if="isDoc(file)" class="col-md-1 icon text-muted"> <!-- Extra div required for conditional FA icons -->
                            <div>
                                <i class="fas fa-file-word"></i>
                            </div>
                        </div>
                        <div v-else-if="isImage(file)" class="col-md-1 icon text-muted"> <!-- Extra div required for conditional FA icons -->
                            <div>
                                <i class="fas fa-image"></i>
                            </div>
                        </div>
                        <div v-else-if="isExcel(file)" class="col-md-1 icon text-muted"> <!-- Extra div required for conditional FA icons -->
                            <div>
                                <i class="fas fa-file-excel"></i>
                            </div>
                        </div>
                        <div v-else-if="isPowerpoint(file)" class="col-md-1 icon text-muted"> <!-- Extra div required for conditional FA icons -->
                            <div>
                                <i class="fas fa-file-powerpoint"></i>
                            </div>
                        </div>
                        <div v-else class="col-md-1 icon text-muted"> <!-- Extra div required for conditional FA icons -->
                            <div>
                                <i class="fas fa-paperclip"></i>
                            </div>
                        </div>

                        <!-- CONTENT -->
                        <div class="col-md-9">
                            <a class="fw-600" :href="file.file_url" target="_blank">{{ file.file_name }}</a>
                            <div class="text-muted">{{ file.file_title }}</div>
                            <div class="author text-muted small">
                            {{ noteDate(file.created_at) }} by {{ file.name }}
                            </div>
                        </div>
                        <div class="col-md-2">
                            <a class="btn btn-light downloadButton" :href="file.file_url" target="_blank">Download</a>
                            <div v-if="canDeleteNote(file)" class="deleteButton" @click="deleteNote(file)">
                                <i class="fas fa-trash-alt"></i>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Text & image notes -->
        <div v-for="note in notes" class="note-container" :key="note.id">
            <!-- TEXT NOTE -->
            <div class="note" v-if="note.type === 'text'">
                <div class="row no-gutters">
                    
                    <!-- ICON -->
                    <div class="col-md-1 icon text-muted">
                        <i class="fas fa-comment-alt"></i>
                    </div>

                    <!-- CONTENT -->
                    <div class="col-md-10">
                        {{ note.text }}
                        <div class="author text-muted small">
                        {{ noteDate(note.created_at) }} by {{ note.name }}
                        </div>
                    </div>
                    <div class="col-md-1" v-if="canDeleteNote(note)">
                        <div class="deleteButton" @click="deleteNote(note)">
                            <i class="fas fa-trash-alt"></i>
                        </div>
                    </div>
                </div>
            </div>

            <!-- IMAGE NOTE -->
            <div class="note" v-else>
                <div class="row no-gutters">
                    <!-- ICON -->
                    <div class="col-md-1 icon text-muted">
                        <i class="fas fa-image"></i>
                    </div>

                    <!-- CONTENT -->
                    <div class="col-md-10">
                        <img class="image" :src="note.image_url">
                        <div class="author text-muted small mt-1">
                        {{ noteDate(note.created_at) }} by {{ note.name }}
                        </div>
                    </div>

                    <!-- DELETE BUTTON -->
                    <div class="col-md-1" v-if="canDeleteNote(note)">
                        <div class="deleteButton" @click="deleteNote(note)">
                            <i class="fas fa-trash-alt"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        props: ['role', 'user', 'changeorder', 'workorder', 'type'],

        created: function() {
            // Not very OO – ideally we'll have "notable" superclass
            // that workorders/changeorders/etc inherit from. 
            if (this.type === "changeorder" || this.type === "selection") {
                this.notable = this.changeorder;
            }
            else {
                this.notable = this.workorder;
            }
        },

        mounted: function() {
        },

        data: function() {
            return {
                notable: null,
                savingNote: false,
                showNoteInput: false,
                showFileTitleInput: false,
                noteContent: '',
                uploadingImage: false,
                uploadingFile: false,
                image: null,
                imagePath: '',
                localImageUrl: '',
                file: null,
                fileName: '',
                fileTitle: '',
            };
        },

        watch: {
            workorder: function(newVal, oldVal) {
                this.notable = newVal;
            },

            changeorder: function(newVal, oldVal) {
                this.notable = newVal;
            },
        },

        computed: {

            files: function() {
                if (!this.notable || !this.notable.notes) {
                    return [];
                }
                return this.notable.notes.filter((note) => {
                    return note.type == 'file';
                });
            },

            notes: function() {
                if (!this.notable || !this.notable.notes) {
                    return [];
                }
                return this.notable.notes.filter((note) => {
                    return note.type != 'file';
                });
            },

            inputImageRef: function() {
                return this.type + 'Image';
            },

            inputFileRef: function() {
                return this.type + 'File';
            },

            imagePickerId: function() {
                return this.type + '' + '-image-' + this.notable.id;
            },

            filePickerId: function() {
                return this.type + '' + '-file-' + this.notable.id;
            },

            notableObjectType: function() {
                if (this.isChangeOrder || this.isSelection) {
                    return "changeorders";
                }
                return "workorders";
            },

            isSelection: function() {
                return this.type === "selection";
            },

            isChangeOrder: function() {
                return this.type === "changeorder";
            },

            isWorkOrder: function() {
                return this.type === "workorder";
            },

            hasFileAttachments: function() {
                for (let index in this.notable.notes) {
                    let note = this.notable.notes[index];
                    if (note.type === 'file') {
                        return true;
                    }
                }
                return false;
            },
        },

        methods: {
            isPDF: function(note) {
                return note.file_name.endsWith('pdf');
            },

            isDoc: function(note) {
                return note.file_name.endsWith('doc') || note.file_name.endsWith('docx');
            },

            isImage: function(note) {
                return note.file_name.endsWith('jpg') ||
                        note.file_name.endsWith('jpeg') ||
                        note.file_name.endsWith('gif') ||
                        note.file_name.endsWith('png') ||
                        note.file_name.endsWith('svg');
            },

            isExcel: function(note) {
                return note.file_name.endsWith('xls') || note.file_name.endsWith('xlsx');
            },

            isPowerpoint: function(note) {
                return note.file_name.endsWith('ppt') || note.file_name.endsWith('pptx');
            },

            noteDate: function(date)
            {
                return moment.utc(date).local().format('MMMM D, YYYY, h:mm A');
            },

            canDeleteNote: function(note) {
                if (this.role === 'owner' || this.role === 'administrator') {
                    return true;
                }

                return note.created_by_user_id == this.user.id;
            },

            resetNotes: function() {
                this.noteContent = '';
                this.fileTitle = '';
                this.file = null;
                this.fileName = '';
                this.uploadingFile = false;
                this.savingNote = false;
                this.showNoteInput = false;
                this.showFileTitleInput = false;
            },

            clickedAddPhoto: function(e) {
                e.preventDefault();
                let pickerId = '#' + this.imagePickerId;
                $(pickerId).click();
            },

            clickedAttachFile: function(e) {
                this.showFileTitleInput = true;
            },

            clickedChooseFile: function(e) {
                e.preventDefault();
                let pickerId = '#' + this.filePickerId;
                $(pickerId).click();
            },

            selectedFile: function(e) {
                e.preventDefault();
                if (this.$refs[this.inputFileRef].files.length) {
                    this.file = this.$refs[this.inputFileRef].files[0];
                    this.fileName = this.file.name;
                }
                else {
                    this.file = null;
                    this.fileName = '';
                }
            },

            clickedUploadFile: function(e) {
                e.preventDefault();
                if (this.$refs[this.inputFileRef].files.length) {
                    this.file = this.$refs[this.inputFileRef].files[0];
                    this.fileName = this.file.name;
                    this.uploadingFile = true;
                    const data = new FormData();
                    data.append('file', this.file);
                    data.append('fileTitle', this.fileTitle);
                    data.append('fileName', this.fileName);
                    data.append('type', 'file');
                    axios.post(`/api/${this.notableObjectType}/${this.notable.id}/note`, data)
                    .then(response => {
                        this.resetNotes();
                        this.emitUpdate(response.data);
                        this.$refs[this.inputFileRef].value = null;
                    })
                    .catch(error => {
                        this.uploadingFile = false;
                        this.$refs[this.inputFileRef].value = null;
                    });
                }
            },

            uploadImage: function(e) {
                e.preventDefault();
                if (this.$refs[this.inputImageRef].files.length) {
                    this.image = this.$refs[this.inputImageRef].files[0];
                    this.imagePath = this.image.name;
                    this.localImageUrl = URL.createObjectURL(this.image);
                    this.uploadingImage = true;

                    console.log("IMAGE:");
                    console.log(this.image);
                    console.log("-----------------------");
                    console.log("IMAGE PATH:");
                    console.log(this.imagePath);
                    console.log("-----------------------");
                    console.log("localImageUrl:");
                    console.log(this.localImageUrl);
                    console.log("-----------------------");

                    const data = new FormData();
                    data.append('image', this.image);
                    data.append('type', 'image');
                    axios.post(`/api/${this.notableObjectType}/${this.notable.id}/note`, data)
                    .then(response => {
                        this.uploadingImage = false;
                        this.emitUpdate(response.data);
                        this.$refs[this.inputImageRef].value = null;
                    })
                    .catch(error => {
                        this.uploadingImage = false;
                        this.$refs[this.inputImageRef].value = null;
                    });
                }
            },

            clickedAddNote: function() {
                this.showNoteInput = true;
            },

            clickedSaveNote: function() {
                this.saveNote();
            },

            clickedCancelNote: function() {
                this.noteContent = '';
                this.fileTitle = '';
                this.fileName = '';
                this.file = null;
                this.showNoteInput = false;
                this.showFileTitleInput = false;
            },

            emitUpdate: function(data) {
                if (this.isChangeOrder) {
                    Bus.$emit('change-order-updated', data);
                }
                else if (this.isSelection) {
                    Bus.$emit('selection-updated', data);
                }
                else {
                    Bus.$emit('work-order-updated', data);
                }
            },

            saveNote: function() {
                this.savingNote = true;
                axios.post(`/api/${this.notableObjectType}/${this.notable.id}/note`, {
                    type: 'text',
                    text: this.noteContent
                })
                .then(response => {
                    this.resetNotes();
                    this.emitUpdate(response.data);
                })
                .catch(error => {
                    this.savingNote = false;
                });
            },

            deleteNote: function(note) {
                // Delete the note locally using Vue.delete so Vue can pick up the changes
                let noteIndex = this.notable.notes.indexOf(note);
                Vue.delete(this.notable.notes, noteIndex);

                axios.delete(`/api/${this.notableObjectType}/${this.notable.id}/note/${note.id}`)    
                .catch(error => {
                    // The delete failed, so put the note back in place
                    Vue.set(this.notable.notes, noteIndex, note);
                });
            },
        }
    }
</script>
